// Cyclone real-time tracking report PDF — same template as the Fire Risk and
// Landslide advisories (orange header, summary card, map, tables, blue footer).
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { preloadReportLogos, HEADER_H } from "../../../common/reportTemplate/reportTemplate.js";
import {
    drawFireHeader, drawFireSummaryCard, drawTwoToneHeading, drawFireFooter,
    formatIssuedAt, FR_CONTENT_BOTTOM,
} from "../../FireRisk/Forecast/pdfLayout_FR.js";

const TEXT = {
    en: {
        heading: "CYCLONE TRACKING TODAY",
        named: "CYCLONE",
        latestUpdate: "Latest update",
        description: "Real-time tracking of active tropical cyclones near Timor-Leste",
        layersShown: "Layers shown",
        basin: "Basin",
        selectedLeft: "SELECTED CYCLONE : ",
        field: "Field",
        value: "Value",
        name: "Name",
        agency: "Agency",
        latestUpdateCol: "Latest Update",
        availableProducts: "Available Products",
        activeDisplay: "Active Display",
        noDataSelected: "No cyclone selected",
        tableLeft: "KEY TABLE : ",
        tableRight: "ACTIVE CYCLONES",
        head: ["Sl No", "Name", "Agency", "Basin", "Latest Update", "Available Products"],
        none: "No active cyclones",
        products: { GF: "Gust Forecast", GH: "Gust History", WP: "Wind Probabilities", RA: "GFS Rainfall" },
        footer: "Monitor official updates and follow instructions from the authorities.",
        page: "Page [i] of [pageCount]",
    },
    tetum: {
        heading: "REGISTU SIKLONE AGORA",
        named: "SIKLONE",
        latestUpdate: "Atualiza foun liu",
        description: "Akompaña tempu-real siklone tropikál ativu sira besik Timor-Leste",
        layersShown: "Kamada hatudu",
        basin: "Besia",
        selectedLeft: "SIKLONE HILI : ",
        field: "Kampu",
        value: "Valór",
        name: "Naran",
        agency: "Ajénsia",
        latestUpdateCol: "Atualiza Foun Liu",
        availableProducts: "Produtu Disponível",
        activeDisplay: "Hatudu Ativo",
        noDataSelected: "La iha siklone hili",
        tableLeft: "TABELA XAVE : ",
        tableRight: "SIKLONE ATIVU",
        head: ["N°", "Naran", "Ajénsia", "Besia", "Atualiza Foun Liu", "Produtu Disponível"],
        none: "Laiha siklone ativu",
        products: { GF: "Previsaun Gust", GH: "Istória Gust", WP: "Probabilidade Anin", RA: "Udan GFS" },
        footer: "Tuir atualizasaun ofisiál no instrusaun husi autoridade sira.",
        page: "Pajina [i] husi [pageCount]",
    },
};

const loadImage = (src) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
});

const productsOf = (cyclone, labels) => {
    const code = cyclone?.tsr_product || "";
    return ["GF", "GH", "WP", "RA"].filter((c) => code.includes(c)).map((c) => labels[c]);
};

/**
 * cyclones: all active cyclone records; selected: the chosen record (or null);
 * activeLayers: codes of the layers currently shown on the map
 * (gustForecast, gustHistory, windProb, gfsRain);
 * mapBlob: PNG blob of the captured map (or null).
 */
export async function generateCycloneTrackingReport({
    language = "en", cyclones = [], selected = null, selectedBasin = "",
    activeLayers = [], mapBlob = null,
}) {
    const t = TEXT[language] || TEXT.en;
    const layerCode = { gustForecast: "GF", gustHistory: "GH", windProb: "WP", gfsRain: "RA" };
    const activeLayerLabels = activeLayers.map((l) => t.products[layerCode[l]] || l);
    const pdf = new jsPDF("portrait", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const headerHeight = HEADER_H + 5;
    const limitY = FR_CONTENT_BOTTOM;
    const logos = await preloadReportLogos();
    const headerOpts = { title: "TIMOR LESTE CYCLONE TRACKING ADVISORY", issuedAt: formatIssuedAt() };
    const drawHeader = () => drawFireHeader(pdf, logos, headerOpts);
    const addPageWithHeader = () => { pdf.addPage(); drawHeader(); return headerHeight + 5; };

    const basin = selected?.tsr_basin || selectedBasin || "";

    // ── Header + summary card ───────────────────────────────────────────────
    drawHeader();
    let currentY = headerHeight + 3;
    const layersLine = activeLayerLabels.length
        ? `${t.layersShown}: ${activeLayerLabels.join(", ")}`
        : t.description;
    currentY = drawFireSummaryCard(pdf, currentY, {
        forecastDate: selected?.latest_update || "—",
        dateLabel: t.latestUpdate,
        levelText: basin || "—",
        levelSub: t.basin,
        heading: selected?.name ? `${t.named} ${selected.name}`.toUpperCase() : t.heading,
        description: layersLine.length > 64 ? `${layersLine.slice(0, 61)}...` : layersLine,
        icon: "cyclone",
    });
    currentY += 4;

    // ── Map capture ─────────────────────────────────────────────────────────
    if (mapBlob) {
        let url = null;
        try {
            url = URL.createObjectURL(mapBlob);
            const img = await loadImage(url);
            const maxW = 158;
            const maxH = 80;
            let w = maxW;
            let h = (img.height / img.width) * w;
            if (h > maxH) { h = maxH; w = (img.width / img.height) * h; }
            pdf.addImage(img, "PNG", (pageWidth - w) / 2, currentY, w, h);
            currentY += h + 7;
        } catch (e) {
            console.warn("[Cyclone PDF] map embed failed:", e);
        } finally {
            if (url) URL.revokeObjectURL(url);
        }
    } else {
        pdf.setFont("helvetica", "italic").setFontSize(9).setTextColor(120, 120, 120);
        pdf.text("Map capture unavailable.", 10, currentY + 5);
        pdf.setTextColor(0, 0, 0);
        currentY += 12;
    }

    // ── Selected cyclone information ────────────────────────────────────────
    const tableStyle = {
        theme: "grid",
        rowPageBreak: "avoid", // never split a row across pages (leaves an empty sliver)
        headStyles: { fillColor: [225, 232, 242], textColor: [0, 0, 0], fontSize: 8.5, cellPadding: { top: 2.2, bottom: 2.2, left: 1.5, right: 1.5 } },
        styles: { fontSize: 8.5, cellPadding: { top: 2.2, bottom: 2.2, left: 1.8, right: 1.8 }, overflow: "linebreak", textColor: [85, 85, 85], lineWidth: 0.25, lineColor: [150, 150, 150], minCellHeight: 8 },
        // Keep rows clear of the footer bar; continuation pages get the header.
        margin: { left: 9, right: 9, top: headerHeight, bottom: pageHeight - FR_CONTENT_BOTTOM },
        didDrawPage: (data) => { if (data.pageNumber > 1) drawHeader(); },
    };
    const headCell = (h) => ({ content: h, styles: { halign: "center", valign: "middle", fontStyle: "bold" } });

    const infoRows = selected ? [
        [t.name, selected.name || "—"],
        [t.agency, selected.agency || "—"],
        [t.basin, selected.tsr_basin || selectedBasin || "—"],
        [t.latestUpdateCol, selected.latest_update || "—"],
        [t.availableProducts, productsOf(selected, t.products).join(", ") || "—"],
        [t.activeDisplay, activeLayerLabels.join(", ") || "—"],
    ] : [[t.noDataSelected, "—"]];

    if (currentY + 30 > limitY) currentY = addPageWithHeader();
    currentY += 3;
    drawTwoToneHeading(pdf, currentY, t.selectedLeft, selected?.name || "—");
    currentY += 4;
    autoTable(pdf, {
        ...tableStyle,
        startY: currentY,
        head: [[headCell(t.field), headCell(t.value)]],
        body: infoRows,
        columnStyles: {
            0: { cellWidth: 60, halign: "left", valign: "middle", fontStyle: "bold", textColor: [0, 0, 0] },
            1: { cellWidth: 132, halign: "left", valign: "middle" },
        },
    });
    currentY = pdf.lastAutoTable.finalY + 9;

    // ── Key table: all active cyclones, selected one highlighted ────────────
    const rows = cyclones.map((c, i) => [
        i + 1,
        c.name || "—",
        c.agency || "—",
        c.tsr_basin || "—",
        c.latest_update || "—",
        productsOf(c, t.products).join(", ") || "—",
    ]);
    const isSelected = (c) => selected && c.name === selected.name && c.agency === selected.agency;
    const selectedRow = cyclones.findIndex(isSelected);

    if (currentY + 30 > limitY) currentY = addPageWithHeader();
    drawTwoToneHeading(pdf, currentY, t.tableLeft, t.tableRight);
    currentY += 4;

    autoTable(pdf, {
        ...tableStyle,
        startY: currentY,
        head: [t.head.map(headCell)],
        body: rows.length ? rows : [[{ content: t.none, colSpan: 6, styles: { halign: "center" } }]],
        columnStyles: {
            0: { cellWidth: 10, halign: "center", valign: "middle" },
            1: { cellWidth: 34, halign: "left", valign: "middle" },
            2: { cellWidth: 22, halign: "left", valign: "middle" },
            3: { cellWidth: 20, halign: "center", valign: "middle" },
            4: { cellWidth: 44, halign: "left", valign: "middle" },
            5: { cellWidth: 62, halign: "left", valign: "middle" },
        },
        didParseCell: (data) => {
            if (data.section === "body" && data.row.index === selectedRow) {
                data.cell.styles.fillColor = [255, 244, 204];
                data.cell.styles.fontStyle = "bold";
                data.cell.styles.textColor = [0, 0, 0];
            }
        },
    });

    // ── Footer + page label on every page ───────────────────────────────────
    const pageCount = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        drawFireFooter(pdf, {
            message: t.footer,
            pageLabel: t.page.replace("[i]", String(i)).replace("[pageCount]", String(pageCount)),
        });
    }

    const dateStr = new Date().toISOString().split("T")[0];
    const name = selected?.name ? selected.name.toLowerCase().replace(/\s+/g, "_") : "all";
    pdf.save(`cyclone_realtime_${name}_${dateStr}.pdf`);
}
